import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';

// Styled Components (mostly kept the same)
const ChatContainer = styled.div`
  position: absolute;
  bottom: 20px; // Always positioned at the bottom
  right: 20px;
  background: #fff;
  border-radius: 8px;
  width: 320px;
  box-shadow: 0 2px 15px rgba(0,0,0,0.2);
  z-index: 1000;
  overflow: hidden;
  // Use display instead of position for showing/hiding
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

// Rest of your styled components remain the same
const ChatHeader = styled.div`
  background-color: var(--primary-color);
  color: white;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const ChatTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
`;

const MessagesContainer = styled.div`
  height: 250px;
  overflow-y: auto;
  padding: 15px;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Message = styled.div`
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 5px;
  word-wrap: break-word;
  line-height: 1.4;
  font-size: 0.9rem;
  
  ${props => props.sender === 'user' ? `
    align-self: flex-end;
    background-color: var(--primary-color);
    color: white;
    border-bottom-right-radius: 4px;
  ` : `
    align-self: flex-start;
    background-color: #e9ecef;
    color: #333;
    border-bottom-left-radius: 4px;
  `}
`;

const InputContainer = styled.div`
  display: flex;
  padding: 10px;
  border-top: 1px solid #eee;
  background-color: white;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  outline: none;
  font-size: 0.9rem;
  
  &:focus {
    border-color: var(--primary-color);
  }
`;

const SendButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  margin-left: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: var(--primary-color);
  }
  
  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }
`;

// Using the same Button style from Heatmap2 component
const Button = styled.button`
  background-color: var(--primary-color);
  border-radius: 10px;
  border: none;
  font-size: 20px;
  padding: 7px 10px;
  width: 13rem;
  margin: 1rem auto;
  cursor: pointer;
  color: var(--light-color);
`;

const ChatAssistant = ({ dataSummary }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { sender: "ai", text: "Hi there! I can help answer questions about your heatmap data. What would you like to know?" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const toggleButtonRef = useRef(null);

  // Toggle the dropdown
  const toggleChat = () => {
    setIsOpen(!isOpen);
    // Clear any error messages when toggling the chat
    if (error) setError(null);
  };
  
  // Explicitly close the chat
  const closeChat = () => {
    setIsOpen(false);
    // Clear any error messages
    if (error) setError(null);
  };

  // We're removing the click-outside handler entirely
  // The chat will now only close when either:
  // 1. The close button in the header is clicked
  // 2. The AI Assistant button is clicked again
  
  // No more automatic closing when clicking elsewhere

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    const question = input.trim();
    setMessages(prev => [...prev, { sender: "user", text: question }]);
    setInput("");
    setIsLoading(true);
    setError(null); // Clear any previous errors

    try {
      // Add a loading indicator message
      setMessages(prev => [...prev, { sender: "ai", text: "..." }]);
      
      console.log("Sending request to backend with data:", { 
        dataSummary: dataSummary ? `${dataSummary.substring(0, 50)}...` : 'No data',
        question
      });
      
      // Make the actual API call
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/chat`, 
        { dataSummary, question },
        { 
          timeout: 30000, // 30 second timeout
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      console.log("Response received:", res.data);
      
      // Replace the loading message with the actual response
      setMessages(prev => [
        ...prev.slice(0, prev.length - 1),
        { sender: "ai", text: res.data.reply }
      ]);
    } catch (err) {
      console.error("Error in chat request:", err);
      
      let errorMessage = "Sorry, I couldn't process your request. Please try again.";
      
      // Extract more helpful error messages if available
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = `Error: ${err.response.data.message}`;
      } else if (err.message === "Network Error") {
        errorMessage = "Network error. Please check your connection.";
      } else if (err.message.includes("timeout")) {
        errorMessage = "Request timed out. The server took too long to respond.";
      }
      
      // Update the UI with the error message
      setMessages(prev => [
        ...prev.slice(0, prev.length - 1),
        { sender: "ai", text: errorMessage }
      ]);
      
      setError(err.message || "Unknown error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      sendMessage();
    }
  };

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <>
      {/* Changed to Button styled component from Heatmap2 and positioned to be below Download SVG button */}
      <div style={{ position: 'absolute', top: '80px', right: '20px', zIndex: 1000 }}>
        <Button
          onClick={toggleChat}
          ref={toggleButtonRef}
        >
          <span role="img" aria-label="AI">🤖</span> AI Assistant
        </Button>
      </div>
      
      <ChatContainer 
        isOpen={isOpen} 
        ref={chatContainerRef}
      >
        <ChatHeader>
          <ChatTitle>AI Map Assistant</ChatTitle>
          <CloseButton onClick={closeChat}>×</CloseButton>
        </ChatHeader>
        
        <MessagesContainer>
          {messages.map((msg, i) => (
            <Message key={i} sender={msg.sender}>
              {msg.text}
            </Message>
          ))}
          <div ref={messagesEndRef} />
        </MessagesContainer>
        
        <InputContainer>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your heatmap data..."
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <SendButton onClick={sendMessage} disabled={isLoading || !input.trim()}>
            ➤
          </SendButton>
        </InputContainer>
      </ChatContainer>
    </>
  );
};

export default ChatAssistant;